import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Card, Col, Flex, Row, Skeleton, Typography } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  fetchQuestionnairesAsAdmin,
  fetchQuestionsAsAdmin,
  fetchResultStatistics,
  fetchUsersAsAdmin,
} from '@/apis';
import { adminLinks } from '@/components/Admin/adminNav';
import PageHeader from '@/components/Admin/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { Path } from '@/enums';

const { Text, Title } = Typography;

interface Stats {
  users: number | null;
  questionnaires: number | null;
  questions: number | null;
  completed: number | null;
}

const emptyStats: Stats = {
  users: null,
  questionnaires: null,
  questions: null,
  completed: null,
};

const fulfilled = <T,>(r: PromiseSettledResult<T>) =>
  r.status === 'fulfilled' ? r.value : null;

const AdminDashboardPage = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState<Stats>(emptyStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [users, questionnaires, questions, results] =
        await Promise.allSettled([
          fetchUsersAsAdmin(1, 1),
          fetchQuestionnairesAsAdmin(1, 1),
          fetchQuestionsAsAdmin(1, 1),
          fetchResultStatistics(),
        ]);
      if (cancelled) return;
      setStats({
        users: fulfilled(users)?.data.total ?? null,
        questionnaires: fulfilled(questionnaires)?.data.total ?? null,
        questions: fulfilled(questions)?.data.total ?? null,
        completed: fulfilled(results)?.data.overview.totalCompleted ?? null,
      });
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const statCards: {
    label: string;
    value: number | null;
    icon: ReactNode;
    to: string;
    color: string;
    bg: string;
  }[] = [
    {
      label: '註冊用戶',
      value: stats.users,
      icon: <TeamOutlined />,
      to: Path.AdminUsers,
      color: '#3b7dd8',
      bg: '#eaf2fc',
    },
    {
      label: '問卷份數',
      value: stats.questionnaires,
      icon: <FileTextOutlined />,
      to: Path.AdminQuestionnaires,
      color: '#5b8a4a',
      bg: '#e9f0e4',
    },
    {
      label: '題目數量',
      value: stats.questions,
      icon: <QuestionCircleOutlined />,
      to: Path.AdminQuestions,
      color: '#d98a1f',
      bg: '#fdf3e1',
    },
    {
      label: '已完成評測',
      value: stats.completed,
      icon: <CheckCircleOutlined />,
      to: Path.AdminResults,
      color: '#8a5cc7',
      bg: '#f2ebfa',
    },
  ];

  // 總覽本身不需要出現在快速前往清單
  const groups = adminLinks
    .filter((link) => link.to !== Path.AdminDashboard)
    .reduce<Record<string, typeof adminLinks>>((acc, link) => {
      const key = link.group ?? '其他';
      (acc[key] ||= []).push(link);
      return acc;
    }, {});

  return (
    <>
      <PageHeader
        title={`歡迎回來，${profile?.name || '管理員'}`}
        description="這裡是後台總覽，可以快速查看系統概況並前往各項管理功能。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {statCards.map((card) => (
          <Col xs={12} lg={6} key={card.label}>
            <Link to={card.to}>
              <Card hoverable styles={{ body: { padding: 20 } }}>
                <Flex align="center" gap={16}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: card.bg,
                      color: card.color,
                      fontSize: 22,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {card.label}
                    </Text>
                    {loading ? (
                      <Skeleton.Input active size="small" block />
                    ) : (
                      <div
                        style={{
                          fontSize: 26,
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {card.value ?? '—'}
                      </div>
                    )}
                  </div>
                </Flex>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {Object.entries(groups).map(([groupName, links]) => (
        <section key={groupName} style={{ marginBottom: 28 }}>
          <Title level={5} style={{ marginBottom: 12 }}>
            {groupName}
          </Title>
          <Row gutter={[16, 16]}>
            {links.map((link) => (
              <Col xs={24} sm={12} xl={8} key={link.to}>
                <Link to={link.to}>
                  <Card hoverable styles={{ body: { padding: 20 } }}>
                    <Flex align="center" gap={16}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: '#e9f0e4',
                          color: '#4d6b3c',
                          fontSize: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {link.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong>{link.label}</Text>
                        <Text
                          type="secondary"
                          style={{ display: 'block', fontSize: 13 }}
                        >
                          {link.description}
                        </Text>
                      </div>
                      <ArrowRightOutlined style={{ color: '#9ca3af' }} />
                    </Flex>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        </section>
      ))}
    </>
  );
};

export default AdminDashboardPage;
